<script setup>
import { computed, ref, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { helpers, required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  documentType: { type: Object, default: null },
  isSubmitting: { type: Boolean, default: false },
});
const emit = defineEmits(['save']);
const { t } = useI18n();

const dialogRef = ref(null);
const keyEdited = ref(false);
const form = ref({ label: '', key: '', description: '', active: true });

const rules = {
  label: { required },
  key: { required, snakeCase: helpers.regex(/^[a-z0-9_]+$/) },
};

const v$ = useVuelidate(rules, form);

const isAddMode = computed(() => props.mode === 'add');

const toSnakeCase = value =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

// Derive the key from the label until the user edits it manually.
watch(
  () => form.value.label,
  label => {
    if (isAddMode.value && !keyEdited.value) {
      form.value.key = toSnakeCase(label);
    }
  }
);

const onKeyInput = () => {
  keyEdited.value = true;
};

const keyMessage = computed(() => {
  if (!v$.value.key.$error) return t('DOCUMENTS.TYPES_SETTINGS.FORM.KEY_HINT');
  return v$.value.key.required.$invalid
    ? t('DOCUMENTS.TYPES_SETTINGS.FORM.KEY_REQUIRED')
    : t('DOCUMENTS.TYPES_SETTINGS.FORM.KEY_INVALID');
});

const open = () => {
  form.value = {
    label: props.documentType?.label || '',
    key: props.documentType?.key || '',
    description: props.documentType?.description || '',
    active: props.documentType?.active ?? true,
  };
  keyEdited.value = false;
  v$.value.$reset();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const submit = async () => {
  if (props.isSubmitting) return;

  const isValid = await v$.value.$validate();
  if (!isValid) return;

  const payload = {
    label: form.value.label.trim(),
    description: form.value.description.trim(),
  };
  if (isAddMode.value) {
    payload.key = form.value.key.trim();
  } else {
    payload.active = form.value.active;
  }

  emit('save', payload);
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isAddMode
        ? t('DOCUMENTS.TYPES_SETTINGS.ADD_DIALOG.TITLE')
        : t('DOCUMENTS.TYPES_SETTINGS.EDIT_DIALOG.TITLE')
    "
    :description="isAddMode ? t('DOCUMENTS.TYPES_SETTINGS.ADD_DIALOG.DESCRIPTION') : ''"
    width="lg"
    :persistent="isSubmitting"
  >
    <div class="flex flex-col gap-4 py-2">
      <div>
        <Input
          v-model="form.label"
          :label="t('DOCUMENTS.TYPES_SETTINGS.FORM.LABEL')"
          :placeholder="t('DOCUMENTS.TYPES_SETTINGS.FORM.LABEL_PLACEHOLDER')"
          :message="v$.label.$error ? t('DOCUMENTS.TYPES_SETTINGS.FORM.LABEL_REQUIRED') : ''"
          :message-type="v$.label.$error ? 'error' : 'info'"
          autofocus
        />
      </div>
      <Input
        v-model="form.key"
        :label="t('DOCUMENTS.TYPES_SETTINGS.FORM.KEY')"
        :message="isAddMode ? keyMessage : ''"
        :message-type="v$.key.$error ? 'error' : 'info'"
        :disabled="!isAddMode"
        custom-input-class="font-mono"
        @input="onKeyInput"
      />
      <TextArea
        v-model="form.description"
        :label="t('DOCUMENTS.TYPES_SETTINGS.FORM.DESCRIPTION')"
        :placeholder="t('DOCUMENTS.TYPES_SETTINGS.FORM.DESCRIPTION_PLACEHOLDER')"
      />
      <div v-if="!isAddMode" class="flex items-center justify-between">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('DOCUMENTS.TYPES_SETTINGS.FORM.ACTIVE') }}
        </span>
        <Switch v-model="form.active" />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          :label="t('DOCUMENTS.CANCEL')"
          ghost
          slate
          :disabled="isSubmitting"
          @click="close"
        />
        <Button
          :label="
            isAddMode
              ? t('DOCUMENTS.TYPES_SETTINGS.ADD_DIALOG.SUBMIT')
              : t('DOCUMENTS.TYPES_SETTINGS.EDIT_DIALOG.SUBMIT')
          "
          :is-loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit"
        />
      </div>
    </template>
  </Dialog>
</template>
